import { PDFDocument, PDFTextField } from 'pdf-lib';

export interface InvoiceData {
  engagementCode: string;
  companyName: string;
  brNumber: string;
  yearEndDate: string;
  invoice: string;
  auditReportDate: string;
  opinion: string;
  typeOfEngagement: string;
}

export interface PDFPreviewResult {
  blobUrl: string;
  pdfBytes: Uint8Array;
}

/**
 * 根据Engagement Code选择合适的PDF模板
 * @param engagementCode 项目代码
 * @returns 模板文件路径
 */
export const getInvoiceTemplate = (engagementCode: string): string => {
  if (engagementCode.startsWith('AUD')) {
    return '/AuditInvoiceTemplate.pdf';
  } else if (engagementCode.startsWith('ACT') || engagementCode.startsWith('GCS')) {
    // 可以根据需要选择consultantsInvoiceTemplate1.pdf或consultantsInvoiceTemplate2.pdf
    // 这里默认使用consultantsInvoiceTemplate1.pdf
    return '/consultantsInvoiceTemplate1.pdf';
  }
  // 默认使用consultantsInvoiceTemplate1.pdf
  return '/consultantsInvoiceTemplate1.pdf';
};

/**
 * 填充PDF表单字段
 * @param pdfBytes 原始PDF字节数组
 * @param data 发票数据
 * @returns 填充后的PDF字节数组
 */
export const fillPDFForm = async (pdfBytes: Uint8Array, data: InvoiceData): Promise<Uint8Array> => {
  // 加载PDF文档
  const pdfDoc = await PDFDocument.load(pdfBytes);
  
  // 获取表单
  const form = pdfDoc.getForm();
  
  // 获取所有字段名称用于调试
  const fields = form.getFields();
  console.log('========== PDF表单字段debug信息 ==========');
  console.log('PDF表单字段總數:', fields.length);
  console.log('PDF表单字段列表:', fields.map(f => ({
    name: f.getName(),
    type: f.constructor.name
  })));
  console.log('==========================================');
  
  // 填充表单字段 - 根据实际PDF模板的字段名称进行填充
  // 注意：字段名称需要根据实际的PDF模板来确定
  try {
    // 尝试填充公司名称
    fillFieldWithVariations(form, data.companyName, [
      'companyName', 'company_name', 'Company Name',
      'company', 'Company', 'client_name', 'Client Name'
    ]);
    
    // 尝试填充项目代码
    fillFieldWithVariations(form, data.engagementCode, [
      'engagementCode', 'engagement_code', 'Engagement Code',
      'engagement', 'Engagement', 'eng_code', 'EngCode'
    ]);
    
    // 尝试填充BR号码
    fillFieldWithVariations(form, data.brNumber, [
      'brNumber', 'br_number', 'BR Number',
      'br', 'BR', 'business_reg', 'Business Registration'
    ]);
    
    // 尝试填充年度结算日期
    fillFieldWithVariations(form, data.yearEndDate, [
      'yearEndDate', 'year_end_date', 'Year End Date',
      'yearend', 'YearEnd', 'year_end', 'Year End'
    ]);
    
    // 尝试填充发票金额
    fillFieldWithVariations(form, data.invoice, [
      'invoice', 'Invoice', 'amount', 'Amount',
      'total', 'Total', 'price', 'Price'
    ]);
    
    // 尝试填充审计报告日期
    fillFieldWithVariations(form, data.auditReportDate, [
      'auditReportDate', 'audit_report_date', 'Audit Report Date',
      'report_date', 'Report Date', 'audit_date', 'Audit Date'
    ]);
    
    // 尝试填充意见
    fillFieldWithVariations(form, data.opinion, [
      'opinion', 'Opinion', 'audit_opinion', 'Audit Opinion'
    ]);
    
    // 尝试填充项目类型
    fillFieldWithVariations(form, data.typeOfEngagement, [
      'typeOfEngagement', 'type_of_engagement', 'Type of Engagement',
      'engagement_type', 'Engagement Type', 'service_type', 'Service Type'
    ]);
    
  } catch (error) {
    console.error('填充PDF表单時出錯:', error);
  }
  
  // 保存填充后的PDF
  return await pdfDoc.save();
};

/**
 * 尝试填充单个字段，如果字段不存在则忽略
 * @param form PDF表单
 * @param fieldName 字段名称
 * @param value 字段值
 */
const tryFillField = (form: any, fieldName: string, value: string): void => {
  try {
    const field = form.getField(fieldName);
    if (field && field instanceof PDFTextField) {
      field.setText(value);
      console.log(`✓ 成功填充字段: ${fieldName} = ${value}`);
    }
  } catch (error) {
    // 字段不存在，忽略
  }
};

/**
 * 尝试用多种字段名称变体填充字段
 * @param form PDF表单
 * @param value 字段值
 * @param variations 字段名称变体数组
 */
const fillFieldWithVariations = (form: any, value: string, variations: string[]): void => {
  let filled = false;
  for (const fieldName of variations) {
    try {
      const field = form.getField(fieldName);
      if (field && field instanceof PDFTextField) {
        field.setText(value);
        console.log(`✓ 成功填充字段: ${fieldName} = ${value}`);
        filled = true;
        break; // 找到并填充成功后，不再尝试其他变体
      }
    } catch (error) {
      // 字段不存在，继续尝试下一个变体
    }
  }
  
  if (!filled) {
    console.warn(`✗ 未找到匹配的字段賴填充值: ${value}`);
    console.warn(`  嘗試的字段名称: ${variations.join(', ')}`);
  }
};

/**
 * 生成预览用的PDF Blob URL
 * @param data 发票数据
 * @returns 包含blobUrl和pdfBytes的对象
 */
export const generateInvoicePreview = async (data: InvoiceData): Promise<PDFPreviewResult> => {
  // 获取模板路径
  const templatePath = getInvoiceTemplate(data.engagementCode);
  
  // 获取模板文件
  const response = await fetch(templatePath);
  if (!response.ok) {
    throw new Error(`無法載入PDF模板: ${templatePath}`);
  }
  
  const templateBytes = await response.arrayBuffer();
  const pdfBytes = new Uint8Array(templateBytes);
  
  // 填充表单
  const filledPdfBytes = await fillPDFForm(pdfBytes, data);
  
  // 创建Blob URL
  const blob = new Blob([filledPdfBytes as any], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);
  
  return {
    blobUrl,
    pdfBytes: filledPdfBytes
  };
};

/**
 * 下载PDF文件
 * @param pdfBytes PDF字节数组
 * @param fileName 文件名
 */
export const downloadPDF = (pdfBytes: Uint8Array, fileName: string): void => {
  const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  
  // 清理
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 清理Blob URL
 * @param blobUrl 要清理的Blob URL
 */
export const revokeBlobUrl = (blobUrl: string): void => {
  URL.revokeObjectURL(blobUrl);
};